import {AttrsSchema, Component} from 'panel';
import range from 'lodash/range';

import {DEFAULT_CHART_HEIGHT} from 'common/report/constants';
import * as colors from 'common/util/colors';
import {defineAndMakeJsxFactory} from 'common/util/jsx';
import type {Section, SectionItem, TooltipInfo} from 'common/components/items-menu/types';

import {ChartLegendTheme, MAX_SEGMENTS_PER_COMPACT_TABLE_ROW} from './constants';
import {template} from './template';
import css from './index.styl?inline';

interface ChartLegendState {
  hiddenSeries: string[];
  searchQuery: string;
}

interface ChartLegendAttributes {
  'background-color'?: string;
  'color-theme'?: string;
  'enable-search'?: boolean;
  sections?: Section[];
  segments?: string[];
  'show-select-all'?: boolean;
  height?: string;
  'wrap-labels'?: boolean;
  'read-only'?: boolean;
  loading?: boolean;
  theme?: ChartLegendTheme;
  'select-all-disabled'?: boolean;
  'select-all-disabled-tooltip'?: TooltipInfo;
}

interface ToggleCheckboxDetail {
  hiddenSeries: string[];
  isSelectAll: boolean;
  item?: SectionItem;
}

export interface ChartLegendJsxProps {
  attrs?: ChartLegendAttributes;
  on?: {
    togglecheckbox?: () => CustomEvent<ToggleCheckboxDetail>;
  };
}

const SEARCH_HEIGHT = 54;

export class ChartLegend extends Component<ChartLegendState, ChartLegendAttributes> {
  public static get attrsSchema(): AttrsSchema<ChartLegendAttributes> {
    return {
      ...super.attrsSchema,
      'background-color': {type: `string`, description: `overrides the background color of the legend`},
      'color-theme': {
        type: `string`,
        default: `default`,
        description: `colors theme of the lines plotted on the legend, should be the same as the line chart`,
      },
      'enable-search': {type: `boolean`, description: `enable search box`},
      sections: {
        type: `json`,
        default: null,
        description: `explicit section data containing information about color and selected segments`,
      },
      segments: {type: `json`, default: [], description: `array of segment names`},
      'show-select-all': {type: `boolean`, description: `show select all checkbox`},
      'select-all-disabled': `boolean`,
      'select-all-disabled-tooltip': `json`,
      height: {
        type: `string`,
        default: null,
        description: `height of the legend, required for progressive functionality`,
      },
      'wrap-labels': {
        type: `boolean`,
        description: `wrap each item so that they fit on multiple lines`,
      },
      'read-only': {type: `boolean`, description: `minimal download friendly display mode`},
      loading: {type: `boolean`, description: `show legend loading styles`},
      theme: {type: `string`, enum: Object.values(ChartLegendTheme), default: ChartLegendTheme.Primary},
    };
  }

  public get config() {
    return {
      css,
      useShadowDom: true,
      template,
      defaultState: {
        hiddenSeries: [],
        searchQuery: ``,
      },
      helpers: {
        MAX_SEGMENTS_PER_COMPACT_TABLE_ROW,
        ChartLegendTheme,
        range,
        getSections: () => {
          if (this.attr(`loading`)) {
            return [];
          }

          const segments = this.attr(`segments`);
          const items = segments.map((segment, segmentIdx) => {
            const colorIdx = segmentIdx % segments.length;
            const color = colors.getChartSegmentColorCombo(colorIdx, this.attr(`color-theme`)).primary;
            return {
              color,
              label: segment,
              labelTheme: `soft`,
              isSelected: !this.state.hiddenSeries.includes(segment),
              wrapLabel: this.attr(`wrap-labels`),
            };
          });
          return [
            {
              items,
            },
          ];
        },

        toggledAllCheckboxes: () => {
          const hiddenSeries = this.state.hiddenSeries.length > 0 ? [] : this.attr(`segments`);
          this.update({hiddenSeries});
          this.dispatchEvent(
            new CustomEvent(`togglecheckbox`, {
              detail: {
                hiddenSeries,
                isSelectAll: true,
              },
            }),
          );
        },

        toggledCheckbox: (ev) => {
          const {item} = ev.detail;
          const originalHiddenSeries = this.state.hiddenSeries;
          let hiddenSeries = originalHiddenSeries;
          if (originalHiddenSeries.includes(item.label)) {
            // make it visible since it's already hidden
            hiddenSeries = originalHiddenSeries.filter((value) => value !== item.label);
          } else {
            // or make it hidden since it's not already
            hiddenSeries.push(item.label);
          }
          this.update({hiddenSeries});
          this.dispatchEvent(
            new CustomEvent(`togglecheckbox`, {
              detail: {
                hiddenSeries,
                isSelectAll: false,
                item,
              },
            }),
          );
        },

        updateLegendSearch: (ev) => {
          this.update({searchQuery: ev.target.value.toLowerCase()});
        },

        overrideStyle: () => {
          return {
            ...(this.attr(`background-color`) && {background: this.attr(`background-color`)}),
          };
        },

        filterSelected: (sections) => {
          return sections.reduce((filtered, section) => {
            const selectedInSection = section.items.filter((item) => item.isSelected);
            if (selectedInSection.length) {
              return [...filtered, {...section, items: selectedInSection}];
            }
            return filtered;
          }, []);
        },

        getItemsMenuHeight: () => {
          const fromTop = this.attr(`enable-search`) ? SEARCH_HEIGHT : 0;
          const height = this.attr(`height`) || DEFAULT_CHART_HEIGHT;
          return `calc(${height} - ${fromTop}px)`;
        },
      },
    };
  }

  elRefs: {[ref: string]: Node} = {};

  setElRef(ref: string, el: Node) {
    this.elRefs[ref] = el;
  }
}

export const ChartLegendEl = defineAndMakeJsxFactory<ChartLegendJsxProps>(`mp-chart-legend`, ChartLegend);
